import classNames from "classnames";
import { Button } from "primereact/button";
import { ChangeEvent, SyntheticEvent, useRef, useState } from "react";
import ImageViewer from "../image-viewer/image-viewer";

export const evaluateUrlImageIsPreviewable = (
  specimen: string | File | undefined
) => {
  if (typeof specimen === "string") {
    return true;
  }

  return false;
};

export interface FormImageProps {
  isMultiple?: boolean;
  urlImage?: string | File;
  label?: string;
  disabled?: boolean;
  imagePreviewClass?: string;
  onImageChange?: (image: File | FileList | undefined) => void;
}

export function ImageField({
  isMultiple,
  label,
  urlImage,
  disabled,
  imagePreviewClass = "w-10rem h-10rem",
  onImageChange,
}: FormImageProps) {
  const [imageFilePreview, setImageFilePreview] = useState<
    string | File | undefined
  >(urlImage);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [onDemandUpload, setOnDemandUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleMultipeImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files) {
      const photoFiles = Array.from(files);
      const previews = photoFiles.map((file) => URL.createObjectURL(file));
      setImagePreviewUrls(previews);
    }

    if (onImageChange && files) {
      onImageChange(files);
    }
  };
  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const image = event?.target?.files[0];
    setImageFilePreview(image);

    if (onImageChange) {
      onImageChange(image);
    }
  };
  const uploadMode = (event: SyntheticEvent) => {
    event.preventDefault();
    setOnDemandUpload(true);
  };
  const handleRemoveImage = (event: SyntheticEvent) => {
    event.preventDefault();

    if (isMultiple && fileInputRef.current) {
      setImagePreviewUrls([]);
      fileInputRef.current.value = "";
      return;
    }

    if (fileInputRef.current) {
      setImageFilePreview(undefined);
      fileInputRef.current.value = "";
    }

    if (onImageChange) {
      onImageChange(undefined);
    }
  };

  const imageViewerElement =
    imageFilePreview && !isMultiple ? (
      <ImageViewer
        className={classNames("w-40 h-40")}
        image={imageFilePreview}
      />
    ) : null;
  const imagesViewerElement =
    imagePreviewUrls && isMultiple ? (
      <div className="mb-4 grid grid-cols-3 gap-2">
        {imagePreviewUrls.map((url, idx) => (
          <img
            key={idx}
            src={url}
            alt={`preview-${idx}`}
            className="w-full h-24 object-cover rounded"
          />
        ))}
      </div>
    ) : null;
  const removeImageButton =
    imageFilePreview || (isMultiple && imagePreviewUrls.length > 0) ? (
      <Button
        className="block"
        label={isMultiple ? "Remove Images" : "Remove Image"}
        onClick={handleRemoveImage}
        severity="secondary"
        outlined
      />
    ) : null;
  const imageInput = (
    <span id="image-input">
      {imageViewerElement}
      {imagesViewerElement}
      <input
        multiple={isMultiple}
        type="file"
        ref={fileInputRef}
        onChange={isMultiple ? handleMultipeImageChange : handleImageChange}
        disabled={disabled}
        className="my-2"
      />
      {removeImageButton}
    </span>
  );
  const imagePreview = (
    <span id="image-preview">
      <img
        src={urlImage as string}
        alt={urlImage as string}
        className={imagePreviewClass}
      />
      <div className="mt-2 flex gap-2">
        <Button onClick={uploadMode} size="small" disabled={disabled} outlined>
          Change
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            window.open(urlImage as string);
          }}
          size="small"
          disabled={disabled}
          severity="secondary"
          outlined
        >
          View
        </Button>
      </div>
    </span>
  );
  const display = () => {
    if (evaluateUrlImageIsPreviewable(urlImage) && !onDemandUpload) {
      return imagePreview;
    }

    return imageInput;
  };

  return (
    <div>
      {label && <label className="text-gray-500 mb-2">{label}</label>}
      <div>{display()}</div>
    </div>
  );
}

export default ImageField;
